import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UrgencyBadge, StatusBadge, typeIcons } from "@/components/Badges";
import { ArrowLeft, Download, Paperclip, MapPin, FileText, Archive } from "lucide-react";

const KNOWN = ["statutory", "license", "contract", "litigation", "resolutions"];
const normType = (tp) => (KNOWN.includes(tp) ? tp : "other");

export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const { data: documents = [], isLoading } = useQuery({ queryKey: ["documents", {}], queryFn: async () => (await api.get("/documents")).data });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const companyMap = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  const doc = documents.find((d) => d.id === id);

  if (isLoading) return <Card className="p-12 text-center text-muted-foreground">…</Card>;

  if (!doc) {
    return (
      <div className="space-y-6" data-testid="document-view-notfound">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="view-back-button" className="text-muted-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("backLabel")}
        </Button>
        <Card className="p-12 text-center text-muted-foreground">{t("noDocuments")}</Card>
      </div>
    );
  }

  const Icon = typeIcons[normType(doc.type)] || FileText;
  const rows = [
    { label: t("company"), value: companyMap[doc.company_id] || "—" },
    { label: t("type"), value: t(normType(doc.type)) },
    { label: t("issueDate"), value: doc.issue_date || "—", mono: true },
    { label: t("expiryDate"), value: doc.expiry_date || "—", mono: true },
    { label: t("responsible"), value: doc.responsible_person || "—" },
    { label: t("counterpart"), value: doc.counterpart || "—" },
  ];

  return (
    <div className="space-y-6" data-testid="document-view-page">
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="view-back-button" className="text-muted-foreground">
        <ArrowLeft className="mr-2 h-4 w-4" /> {t("backLabel")}
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-horizonte-orange">
            <Icon className="h-4 w-4" /> {t("documentDetails")}
            {doc.archived && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                <Archive className="h-3 w-3" /> {t("archivedStatus")}
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="view-doc-title">{doc.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <UrgencyBadge urgency={doc.urgency} testId="view-urgency" />
            <StatusBadge status={doc.status} />
          </div>
        </div>
        {doc.file && (
          <a href={fileUrl(doc.file.storage_path)} target="_blank" rel="noreferrer" data-testid="view-download-button">
            <Button className="bg-horizonte-orange text-white hover:bg-renco-slate">
              <Download className="mr-2 h-4 w-4" /> {t("download")}
            </Button>
          </a>
        )}
      </div>

      <Card className="divide-y p-0">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{r.label}</div>
            <div className={`sm:col-span-2 ${r.mono ? "font-mono text-sm" : ""}`}>{r.value}</div>
          </div>
        ))}
        <div className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("physicalLocation")}</div>
          <div className="sm:col-span-2">
            {doc.physical_location ? (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-horizonte-orange" />{doc.physical_location}</span>
            ) : "—"}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("notes")}</div>
          <div className="sm:col-span-2 whitespace-pre-wrap">{doc.notes || "—"}</div>
        </div>
        <div className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-3 sm:gap-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("attachment")}</div>
          <div className="sm:col-span-2">
            {doc.file ? (
              <a href={fileUrl(doc.file.storage_path)} target="_blank" rel="noreferrer" data-testid="view-attachment-link" className="inline-flex items-center gap-2 font-medium text-horizonte-orange hover:underline">
                <Paperclip className="h-4 w-4" /> {doc.file.original_filename}
              </a>
            ) : t("none")}
          </div>
        </div>
      </Card>
    </div>
  );
}
